import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { clearTokens, isTokenExpired, storeLoginUrlPath, getTokenDetails } from '../../utils/authflow';
import './LandingPage.css';
import RelatedLinksList from "../../assets/RelatedLinksList.json";
import AppTile from './AppTile';
import Redirect from '../Theme/Redirect';
import { getQueryparam } from '../Theme/UrlLoader';
import RelatedLinks from './RelatedLinks';
import { checkUserEmail, getFavourtieApps, setUnsetFavouriteApps } from './userApiCalls';
import { fetchApplications } from '../EditApplication/applicationApiCalls';

const LandingPage = () => {
  const [form, setForm] = useState({ url: "", authMessage: "", newTab: "" });
  const [isEmailPresent, setEmailPresent] = useState(false);
  const [appList, setAppList] = useState([]);
  const [clonedAppList, setClonedAppList] = useState([]);
  const [favoriteApps, setFavoriteApps] = useState([]);
  const [nonFavoriteApps, setNonFavoriteApps] = useState([]);
  const [favoriteAppIds, setFavoriteAppIds] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();
  const tokenDetails = getTokenDetails();
  const formRef = useRef(null);
  const queryParam = getQueryparam(location);

  const submitForm = (data) => {
    setForm(data)
    setTimeout(() => formRef.current.submit(), 100);
  };

  useEffect(() => {
    const fetchData = async () => {
      await setEmailPresentFlag();
      await fetchApplicationsFromBackend();
      await getFavouriteAppsFromDB();
    };

    fetchData();

    if (isTokenExpired()) {
      storeLoginUrlPath(location.pathname);
      clearTokens();
      navigate('/');
    }
  }, []);

  useEffect(() => {
    const updatedClonedList = clone(appList);
    const appType = window.location.pathname === "/ROS/internal/equinor" ? "statoil" : "";
    const wellLinkRSApp = getAppDetails(appType, updatedClonedList);
    setClonedAppList(wellLinkRSApp);
  }, [appList]);

  useEffect(() => {
    reArrangeApps(favoriteAppIds);
  }, [favoriteAppIds, clonedAppList]);

  const setEmailPresentFlag = async () => {
    try {
      const email = tokenDetails?.idToken?.claims?.email;
      const token = tokenDetails?.accessToken?.accessToken;
      const response = await checkUserEmail(email, token);
      setEmailPresent(response.recordfound);
    } catch (error) {
      console.error("Error checking email:", error);
    }
  };

  const arrayBufferToBase64 = (buffer) => {
    const bytes = new Uint8Array(buffer);
    const chunkSize = 0x8000;
    let binary = '';
    for (let i = 0; i < bytes.length; i += chunkSize) {
      const chunk = bytes.subarray(i, i + chunkSize);
      binary += String.fromCharCode.apply(null, chunk);
    }
    return window.btoa(binary);
  };

  const getImageUrl = async (id, token) => {
    try {
      const res = await fetch(`/ros_be_app_directory/applications/${id}/image`,
        {
          headers: { Authorization: `Bearer ${token}` },
          cache: 'no-store',
        }
      );

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }

      const contentType = res.headers.get('content-type') || 'image/png';
      const buffer = await res.arrayBuffer();
      const base64 = arrayBufferToBase64(buffer);

      return `data:${contentType};base64,${base64}`;
    } catch (error) {
      console.error(`Error loading image for app ${id}:`, error);
      return null;
    }
  };

  const fetchApplicationsFromBackend = async () => {
    try {
      const token = tokenDetails?.accessToken?.accessToken;
      const email = tokenDetails?.idToken?.claims?.email;
      const response = await fetchApplications(token, email);

      if (Array.isArray(response)) {
        const extractedApps = await Promise.all(
          response.map(async (app) => ({
            ...app.data,
            image: await getImageUrl(app.id, token),
          }))
        );
        setAppList(extractedApps);
      } else {
        setAppList([]);
      }
    } catch (error) {
      console.error("Error fetching applications:", error);
      setAppList([]);
    }
  };



  const getFavouriteAppsFromDB = async () => {
    try {
      const email = tokenDetails?.idToken?.claims?.email;
      const token = tokenDetails?.accessToken?.accessToken;
      const response = await getFavourtieApps(email, token);
      const appIds = response?.[0]?.appids || [];
      setFavoriteAppIds(appIds);
    } catch (error) {
      console.error("Error fetching favorite apps:", error);
    }
  };

  const sortByDisplayOrder = (apps) =>
    apps.sort((a, b) => (a.display_order || 0) - (b.display_order || 0));


  const reArrangeApps = (favoriteAppIds) => {
    const favoriteApps = sortByDisplayOrder(
      clonedAppList.filter((app) => favoriteAppIds.includes(app.id))
    );

    const nonFavoriteApps = sortByDisplayOrder(
      clonedAppList.filter((app) => !favoriteAppIds.includes(app.id))
    );

    setFavoriteApps(favoriteApps);
    setNonFavoriteApps(nonFavoriteApps);
  };


  const addToFavorites = async (appid) => {
    try {
      const email = tokenDetails?.idToken?.claims?.email;
      const token = tokenDetails?.accessToken?.accessToken;
      await setUnsetFavouriteApps(email, appid, true, token);
      setFavoriteAppIds([...favoriteAppIds, appid]);
    } catch (error) {
      console.error("Error adding to favorites:", error);
    }
  };

  const removeFavorite = async (appid) => {
    try {
      const email = tokenDetails?.idToken?.claims?.email;
      const token = tokenDetails?.accessToken?.accessToken;
      await setUnsetFavouriteApps(email, appid, false, token);
      setFavoriteAppIds(favoriteAppIds.filter((id) => id !== appid));
    } catch (error) {
      console.error("Error removing from favorites:", error);
    }
  };

  const clone = (obj) => JSON.parse(JSON.stringify(obj));

  const getAppDetails = (appType, AppList) => {
    return AppList.filter((app) => {
      const filteredInstances = app.app_instances.filter((instance) => {
        const instanceAppType = instance.app_type;
        return appType === 'statoil'
          ? instanceAppType === 'statoil' || instanceAppType === 'all'
          : !instanceAppType || instanceAppType === 'all';
      });
      app.app_instances = filteredInstances;
      return filteredInstances.length > 0;
    });
  };

  if (isTokenExpired()) {
    return <Redirect />;
  }

  return (
    <>
      {queryParam != null && <h2 className="redirect">
        Redirect... Please Wait...
      </h2>}
      <div style={{ visibility: queryParam !== null ? 'hidden' : 'visible' }} className="row">
        <form ref={formRef} target={form.newTab} method="post" action={form.url} encType="application/x-www-form-urlencoded">
          <input type="hidden" name="AuthMessage" value={form.authMessage} />
        </form>
        {favoriteApps.map((row, index) => (
          <AppTile key={index} {...row} removeFavorite={removeFavorite} setFormData={submitForm} />
        ))}
        {nonFavoriteApps.map((row, index) => (
          <AppTile key={index} {...row} addToFavorites={addToFavorites} setFormData={submitForm} />
        ))}
      </div>
      <div style={{ visibility: queryParam !== null ? 'hidden' : 'visible' }} className="card-tile-50 row">
        {isEmailPresent && <RelatedLinks relatedLinksList={RelatedLinksList} />}
      </div>
    </>
  );
};

export default LandingPage;